import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Link,
} from '@mui/material';
import {
  Restaurant as RestaurantIcon,
  Kitchen as KitchenIcon,
  Group as GroupIcon,
  Star as StarIcon,
} from '@mui/icons-material';
import { useRecipe } from '../context/RecipeContext';
import landingBackground from '../images/landingbackground3.jpg';

const About: React.FC = () => {
  const navigate = useNavigate();
  const { setMode } = useRecipe();

  const handleGetStarted = () => {
    setMode('generate');
    navigate('/composer');
  };

  return (
    <>
      <Helmet>
        <title>About Us - Create A Dish | AI-Powered Recipe Platform by AI Sure Tech</title>
        <meta name="description" content="Learn about Create A Dish, your AI-powered culinary companion for personalized recipe creation. Built by AI Sure Tech, part of a network of innovative AI-driven tools for finance, education, and lifestyle." />
        <meta name="keywords" content="about create a dish, AI recipe generator, AI Sure Tech, recipe platform, personalized cooking, AI cooking assistant, culinary AI technology" />
        <link rel="canonical" href="https://createadish.com/about" />
        <meta property="og:title" content="About Us - Create A Dish | AI-Powered Recipe Platform" />
        <meta property="og:description" content="Discover how Create A Dish uses AI to create personalized recipes tailored to your preferences. Part of the AI Sure Tech network." />
        <meta property="og:url" content="https://createadish.com/about" />
        <meta property="og:type" content="website" />
        <meta name="twitter:title" content="About Us - Create A Dish | AI-Powered Recipe Platform" />
        <meta name="twitter:description" content="Discover how Create A Dish uses AI to create personalized recipes tailored to your preferences." />
      </Helmet>
      <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', maxWidth: '1200px', mx: 'auto' }}>
      {/* Hero Section */}
      <Box
        sx={{
          height: { xs: '40vh', md: '50vh' },
          backgroundImage: `url(${landingBackground})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          filter: 'blur(1px)',
          position: 'relative',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            px: 2,
            textAlign: 'center',
          }}
        >
          <Typography
            variant="h3"
            component="h1"
            sx={{
              color: 'white',
              fontWeight: 'bold',
              textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
              mb: 2,
            }}
          >
            About Us
          </Typography>
          <Typography
            variant="h5"
            sx={{
              color: 'white',
              fontWeight: 'bold',
              textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
              mb: 1,
            }}
          >
            Crafting Perfect Recipes, One Dish at a Time
          </Typography>
        </Box>
      </Box>

      {/* Content Section */}
      <Box sx={{ p: { xs: 2, md: 4 }, flexGrow: 1 }}>
        <Typography
          variant="h4"
          component="h2"
          sx={{
            textAlign: 'center',
            mb: 4,
            fontWeight: 'bold',
            color: 'primary.main'
          }}
        >
          Welcome to Create A Dish
        </Typography>

        <Typography
          variant="body1"
          sx={{
            mb: 4,
            textAlign: 'center',
            maxWidth: '800px',
            mx: 'auto',
            lineHeight: 1.6,
            fontSize: { xs: '1rem', md: '1.1rem' }
          }}
        >
          Create A Dish is your ultimate culinary companion, designed to help home cooks and food enthusiasts
          discover and create delicious recipes tailored to their preferences. Whether you're looking for
          inspiration or have specific ingredients in mind, our platform makes cooking fun and accessible.
        </Typography>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
            gap: 3,
            mb: 4
          }}
        >
          <Card sx={{ textAlign: 'center' }}>
            <CardContent sx={{ p: 3 }}>
              <RestaurantIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
              <Typography variant="h6" component="h3" gutterBottom>
                Smart Recipe Generation
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Our advanced algorithm creates personalized recipes based on your selected ingredients,
                cuisine preferences, and dietary requirements.
              </Typography>
            </CardContent>
          </Card>

          <Card sx={{ textAlign: 'center' }}>
            <CardContent sx={{ p: 3 }}>
              <KitchenIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
              <Typography variant="h6" component="h3" gutterBottom>
                Step-by-Step Guidance
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Follow clear, easy-to-understand instructions with our interactive cook mode that guides
                you through each step of your culinary journey.
              </Typography>
            </CardContent>
          </Card>

          <Card sx={{ textAlign: 'center' }}>
            <CardContent sx={{ p: 3 }}>
              <GroupIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
              <Typography variant="h6" component="h3" gutterBottom>
                Community Driven
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Join a community of food lovers sharing recipes, tips, and culinary experiences.
                Save your favorites and discover new dishes daily.
              </Typography>
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ textAlign: 'center', mb: 4, py: 4, backgroundColor: 'grey.50', borderRadius: 2 }}>
          <Typography
            variant="h5"
            component="h3"
            sx={{
              mb: 3,
              fontWeight: 'bold',
              color: 'primary.main'
            }}
          >
            Website by AI Sure Tech
          </Typography>
          <Typography
            variant="body1"
            sx={{
              mb: 3,
              maxWidth: '900px',
              mx: 'auto',
              lineHeight: 1.6,
              fontSize: { xs: '1rem', md: '1.1rem' },
              px: 2
            }}
          >
            At AISureTech, we specialize in building robust backend systems that seamlessly integrate AI capabilities into your website. Whether you're looking to enable smart chat interfaces, advanced research tools, or custom AI-driven features, our solutions are designed for scalability, security, and performance. Let your site do more—with AI at its core.
          </Typography>
          <Button
            variant="outlined"
            color="primary"
            href="https://aisuretech.com/"
            target="_blank"
            rel="noopener noreferrer nofollow"
            sx={{
              minWidth: 200,
              fontSize: '1rem',
              py: 1.5,
              px: 3,
            }}
          >
            Visit AISureTech
          </Button>
        </Box>

        {/* AI Sure Tech Websites Section */}
        <Box sx={{ mb: 6 }}>
          <Typography
            variant="h5"
            component="h3"
            sx={{
              textAlign: 'center',
              mb: 1,
              fontWeight: 'bold',
              color: 'primary.main'
            }}
          >
            AI Sure Tech Website Network
          </Typography>
          <Typography
            variant="body1"
            sx={{
              mb: 4,
              textAlign: 'center',
              maxWidth: '800px',
              mx: 'auto',
              lineHeight: 1.6,
              fontSize: { xs: '1rem', md: '1.1rem' }
            }}
          >
            Discover AI-driven tools, education, and insights for your growth.
          </Typography>

          {/* Overarching Hub */}
          <Box sx={{ mb: 5 }}>
            <Typography
              variant="h6"
              component="h4"
              sx={{
                mb: 2,
                fontWeight: 'bold',
                color: 'primary.main'
              }}
            >
              Overarching Hub
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }, gap: 3 }}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', backgroundColor: 'white', boxShadow: 3 }}>
                <CardContent sx={{ p: 3, flexGrow: 1 }}>
                  <Typography variant="h6" component="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
                    <Link href="https://aisuretech.com/" target="_blank" rel="noopener noreferrer nofollow" sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                      AI Sure Tech
                    </Link>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                    AI-Powered Website Network. Discover AI-driven tools, education, and insights for your growth.
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          </Box>

          {/* Finance & Investing */}
          <Box sx={{ mb: 5 }}>
            <Typography
              variant="h6"
              component="h4"
              sx={{
                mb: 2,
                fontWeight: 'bold',
                color: 'primary.main'
              }}
            >
              Finance & Investing
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }, gap: 3 }}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', backgroundColor: 'white', boxShadow: 3 }}>
                <CardContent sx={{ p: 3, flexGrow: 1 }}>
                  <Typography variant="h6" component="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
                    <Link href="https://aistocktickers.com/" target="_blank" rel="noopener noreferrer nofollow" sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                      AI Stock Tickers
                    </Link>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                    Your go-to platform for stock analysis, trading, and AI-powered insights. Discover top-performing stocks with daily AI-driven insights.
                  </Typography>
                </CardContent>
              </Card>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', backgroundColor: 'white', boxShadow: 3 }}>
                <CardContent sx={{ p: 3, flexGrow: 1 }}>
                  <Typography variant="h6" component="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
                    <Link href="https://learn.aistocktickers.com/" target="_blank" rel="noopener noreferrer nofollow" sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                      Learn AI Stock Tickers
                    </Link>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                    Master Stock Market Investing with AI. Transform your investment journey with comprehensive educational resources powered by cutting-edge AI analysis.
                  </Typography>
                </CardContent>
              </Card>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', backgroundColor: 'white', boxShadow: 3 }}>
                <CardContent sx={{ p: 3, flexGrow: 1 }}>
                  <Typography variant="h6" component="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
                    <Link href="https://www.finsterchat.com/" target="_blank" rel="noopener noreferrer nofollow" sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                      Finster Chat
                    </Link>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                    Transform Your Future with Finster Chat. Finster provides expert guidance on wealth management, investment strategies, debt management, and retirement planning.
                  </Typography>
                </CardContent>
              </Card>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', backgroundColor: 'white', boxShadow: 3 }}>
                <CardContent sx={{ p: 3, flexGrow: 1 }}>
                  <Typography variant="h6" component="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
                    <Link href="https://cryptiment.com/" target="_blank" rel="noopener noreferrer nofollow" sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                      Cryptiment
                    </Link>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                    Advanced cryptocurrency sentiment analysis powered by real-time data. Track crypto market sentiment and discover trending cryptocurrencies.
                  </Typography>
                </CardContent>
              </Card>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', backgroundColor: 'white', boxShadow: 3 }}>
                <CardContent sx={{ p: 3, flexGrow: 1 }}>
                  <Typography variant="h6" component="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
                    <Link href="https://blog.aistocktickers.com/" target="_blank" rel="noopener noreferrer nofollow" sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                      AI Stock Tickers Blog
                    </Link>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                    AI-Powered Market Intelligence & Insights. Discover cutting-edge analysis on AI-driven stock trading and algorithmic strategies.
                  </Typography>
                </CardContent>
              </Card>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', backgroundColor: 'white', boxShadow: 3 }}>
                <CardContent sx={{ p: 3, flexGrow: 1 }}>
                  <Typography variant="h6" component="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
                    <Link href="https://aimortgagecalc.com/" target="_blank" rel="noopener noreferrer nofollow" sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                      AI Mortgage Calc
                    </Link>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                    Calculate your mortgage payments with precision. Get detailed amortization schedules and AI-powered explanations tailored to your situation.
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          </Box>

          {/* Critical Thinking & Debate */}
          <Box sx={{ mb: 5 }}>
            <Typography
              variant="h6"
              component="h4"
              sx={{
                mb: 2,
                fontWeight: 'bold',
                color: 'primary.main'
              }}
            >
              Critical Thinking & Debate
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }, gap: 3 }}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', backgroundColor: 'white', boxShadow: 3 }}>
                <CardContent sx={{ p: 3, flexGrow: 1 }}>
                  <Typography variant="h6" component="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
                    <Link href="https://www.balanceddebate.com/" target="_blank" rel="noopener noreferrer nofollow" sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                      Balanced Debate
                    </Link>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                    Explore diverse perspectives through structured debate. Our platform leverages advanced AI to present balanced arguments on important topics.
                  </Typography>
                </CardContent>
              </Card>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', backgroundColor: 'white', boxShadow: 3 }}>
                <CardContent sx={{ p: 3, flexGrow: 1 }}>
                  <Typography variant="h6" component="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
                    <Link href="https://opposingpoint.com/" target="_blank" rel="noopener noreferrer nofollow" sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                      Opposing Point
                    </Link>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                    Escape the Echo Chamber and Challenge Your Ideas with AI Precision. Our AI modes challenge your thinking and expose logical fallacies.
                  </Typography>
                </CardContent>
              </Card>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', backgroundColor: 'white', boxShadow: 3 }}>
                <CardContent sx={{ p: 3, flexGrow: 1 }}>
                  <Typography variant="h6" component="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
                    <Link href="https://ideobridge.com/" target="_blank" rel="noopener noreferrer nofollow" sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                      Ideo Bridge
                    </Link>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                    Bridge ideological divides through respectful dialogue. We curate daily opinions from diverse perspectives and generate thoughtful counter-responses.
                  </Typography>
                </CardContent>
              </Card>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', backgroundColor: 'white', boxShadow: 3 }}>
                <CardContent sx={{ p: 3, flexGrow: 1 }}>
                  <Typography variant="h6" component="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
                    <Link href="https://astoicsays.com/" target="_blank" rel="noopener noreferrer nofollow" sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                      A Stoic Says
                    </Link>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                    Today's headlines paired with timeless Stoic wisdom. Daily opinion pieces with reflections inspired by Marcus Aurelius and Epictetus.
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          </Box>

          {/* History & Exploration */}
          <Box sx={{ mb: 5 }}>
            <Typography
              variant="h6"
              component="h4"
              sx={{
                mb: 2,
                fontWeight: 'bold',
                color: 'primary.main'
              }}
            >
              History & Exploration
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }, gap: 3 }}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', backgroundColor: 'white', boxShadow: 3 }}>
                <CardContent sx={{ p: 3, flexGrow: 1 }}>
                  <Typography variant="h6" component="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
                    <Link href="https://althistai.com/" target="_blank" rel="noopener noreferrer nofollow" sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                      Alternate History AI
                    </Link>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                    Explore the paths not taken through artificial intelligence.
                  </Typography>
                </CardContent>
              </Card>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', backgroundColor: 'white', boxShadow: 3 }}>
                <CardContent sx={{ p: 3, flexGrow: 1 }}>
                  <Typography variant="h6" component="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
                    <Link href="https://althistai.aisuretech.com/" target="_blank" rel="noopener noreferrer nofollow" sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                      AltHistAI Explore
                    </Link>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                    Explore the Paths Not Taken. Discover how history could have unfolded differently with AI-powered interactive simulations.
                  </Typography>
                </CardContent>
              </Card>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', backgroundColor: 'white', boxShadow: 3 }}>
                <CardContent sx={{ p: 3, flexGrow: 1 }}>
                  <Typography variant="h6" component="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
                    <Link href="https://www.aievolutionexplorer.com/" target="_blank" rel="noopener noreferrer nofollow" sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                      AI Evolution Explorer
                    </Link>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                    Discover the evolutionary history of animals, explore fascinating facts, and learn about the diversity of life on Earth.
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          </Box>

          {/* Fun, Lifestyle & Miscellaneous */}
          <Box sx={{ mb: 5 }}>
            <Typography
              variant="h6"
              component="h4"
              sx={{
                mb: 2,
                fontWeight: 'bold',
                color: 'primary.main'
              }}
            >
              Fun, Lifestyle & Miscellaneous
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }, gap: 3 }}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', backgroundColor: 'white', boxShadow: 3 }}>
                <CardContent sx={{ p: 3, flexGrow: 1 }}>
                  <Typography variant="h6" component="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
                    <Link href="https://featherguess.com/" target="_blank" rel="noopener noreferrer nofollow" sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                      Feather Guess
                    </Link>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                    Test your bird knowledge. Guess the feather!
                  </Typography>
                </CardContent>
              </Card>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', backgroundColor: 'white', boxShadow: 3 }}>
                <CardContent sx={{ p: 3, flexGrow: 1 }}>
                  <Typography variant="h6" component="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
                    <Link href="https://www.createadish.com/" target="_blank" rel="noopener noreferrer nofollow" sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                      Create A Dish
                    </Link>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                    Your ultimate culinary companion for personalized recipe creation. Whether you're looking for inspiration or have specific ingredients in mind, our platform makes cooking fun and accessible.
                  </Typography>
                </CardContent>
              </Card>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', backgroundColor: 'white', boxShadow: 3 }}>
                <CardContent sx={{ p: 3, flexGrow: 1 }}>
                  <Typography variant="h6" component="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
                    <Link href="https://www.kitty-vids.com/" target="_blank" rel="noopener noreferrer nofollow" sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                      Kitty Vids
                    </Link>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                    Your ultimate destination for adorable cat videos and comprehensive cat care guides. Discover the cutest cat moments and expert advice.
                  </Typography>
                </CardContent>
              </Card>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', backgroundColor: 'white', boxShadow: 3 }}>
                <CardContent sx={{ p: 3, flexGrow: 1 }}>
                  <Typography variant="h6" component="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
                    <Link href="https://www.puppy-vids.com/" target="_blank" rel="noopener noreferrer nofollow" sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                      Puppy Vids
                    </Link>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                    Your ultimate destination for adorable dog videos and comprehensive dog care guides. Join our dog-loving community!
                  </Typography>
                </CardContent>
              </Card>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', backgroundColor: 'white', boxShadow: 3 }}>
                <CardContent sx={{ p: 3, flexGrow: 1 }}>
                  <Typography variant="h6" component="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
                    <Link href="https://www.aitrendified.com/" target="_blank" rel="noopener noreferrer nofollow" sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                      AI Trendified
                    </Link>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                    Today's Trending Digest. Explore the most talked-about topics with matching TED talks and AI insights.
                  </Typography>
                </CardContent>
              </Card>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', backgroundColor: 'white', boxShadow: 3 }}>
                <CardContent sx={{ p: 3, flexGrow: 1 }}>
                  <Typography variant="h6" component="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
                    <Link href="https://www.aiwisdomcouncil.com/" target="_blank" rel="noopener noreferrer nofollow" sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                      AI Wisdom Council
                    </Link>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                    Choose an advisor and begin asking your questions — each member of the Wisdom Council offers a unique perspective.
                  </Typography>
                </CardContent>
              </Card>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', backgroundColor: 'white', boxShadow: 3 }}>
                <CardContent sx={{ p: 3, flexGrow: 1 }}>
                  <Typography variant="h6" component="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
                    <Link href="https://www.ideatomarketai.com/" target="_blank" rel="noopener noreferrer nofollow" sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                      Idea To Market AI
                    </Link>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                    We believe that everyone with a great business idea deserves access to professional-grade market research.
                  </Typography>
                </CardContent>
              </Card>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', backgroundColor: 'white', boxShadow: 3 }}>
                <CardContent sx={{ p: 3, flexGrow: 1 }}>
                  <Typography variant="h6" component="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
                    <Link href="https://www.policyclown.com/" target="_blank" rel="noopener noreferrer nofollow" sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                      Policy Clown
                    </Link>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                    Daily satirical political predictions powered by artificial intelligence. Absurd, exaggerated policy forecasts for entertainment.
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          </Box>
        </Box>

        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography
            variant="h5"
            component="h3"
            sx={{
              mb: 3,
              fontWeight: 'bold',
              color: 'primary.main'
            }}
          >
            Ready to Create Your Perfect Dish?
          </Typography>
          <Button
            variant="contained"
            size="large"
            startIcon={<StarIcon />}
            onClick={handleGetStarted}
            sx={{
              minWidth: 200,
              fontSize: '1.1rem',
              py: 1.5,
              px: 4,
            }}
          >
            Get Started
          </Button>
        </Box>

        <Box sx={{ textAlign: 'center', mb: 4, py: 3, borderTop: 1, borderBottom: 1, borderColor: 'divider' }}>
          <Typography
            variant="h5"
            component="h3"
            sx={{
              mb: 3,
              fontWeight: 'bold',
              color: 'primary.main'
            }}
          >
            Learn More About Us
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'center',
              gap: 2,
              mb: 2
            }}
          >
            <Button
              variant="outlined"
              onClick={() => navigate('/contact')}
              sx={{ minWidth: 120 }}
            >
              Contact Us
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigate('/privacy')}
              sx={{ minWidth: 120 }}
            >
              Privacy Policy
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigate('/terms')}
              sx={{ minWidth: 120 }}
            >
              Terms & Conditions
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigate('/faq')}
              sx={{ minWidth: 120 }}
            >
              FAQ
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigate('/resources')}
              sx={{ minWidth: 120 }}
            >
              Resources
            </Button>
          </Box>
        </Box>

        <Box sx={{ textAlign: 'center', py: 3, borderTop: 1, borderColor: 'divider' }}>
          <Typography variant="body2" color="text.secondary">
            © 2025 Create A Dish. Made with ❤️ for food lovers everywhere.
          </Typography>
        </Box>
      </Box>
      </Box>
    </>
  );
};

export default About;